import { useState } from 'react';

/**
 * useFirebaseFileUpload
 * ---------------------
 * A React hook that manages file uploads via a **signed URL backend flow**
 * and exposes a **task-based upload model** suitable for UI-driven workflows
 * (dropzones, forms, wizards, etc.).
 *
 * ## Mental model
 * - Each file upload is represented as an `UploadTask`
 * - Tasks are created via `addFiles`
 * - Tasks expose imperative controls: `start` and `cancel`
 * - Upload progress and status are driven by XHR events
 *
 * ## Upload flow
 * 1. `getUploadUrl(file)` is called to retrieve a signed GCS write URL,
 *    a storage path, and a public URL from the backend
 * 2. An XHR PUT is issued directly to the signed URL
 * 3. Progress, success, error, and cancel states are mapped back to
 *    the task via React state updates
 *
 * ## Why this exists
 * - All file uploads must go through backend authorization
 * - UI components want simple, declarative state
 * - The signed URL approach enforces backend-controlled access without
 *   requiring the client to hold Firebase credentials
 *
 * ## Usage
 *
 *   const { tasks, addFiles, isUploading } = useFirebaseFileUpload({
 *     getUploadUrl: async (file) => myApi.getSignedUploadUrl(file.name, file.type),
 *   });
 *   addFiles([{ file }]);
 *
 * ## Implementation notes
 * - One XHR instance is created per file
 * - XHR is captured in a closure and never exposed on the task
 * - React state stores a lightweight `UploadTask` abstraction for rendering
 * - Progress is normalized to a 0–100 percentage
 * - `autoStart` controls whether uploads begin immediately after URL resolution
 *
 * ## Non-goals
 * - This hook does not validate files (handled by Dropzone)
 * - This hook does not handle retries or backoff (can be layered on later)
 * - This hook does not assume any UI
 */
function useFirebaseFileUpload({ getUploadUrl, autoStart = true, }) {
    const [tasks, setTasks] = useState([]);
    const addFiles = (inputs) => {
        inputs.forEach(({ file }) => {
            const id = crypto.randomUUID();
            const xhr = new XMLHttpRequest();
            // `started` tracks whether xhr.send() should be called.
            // Initialized to autoStart so .then() always sends when autoStart is true.
            // start() sets it to true; if the XHR is already open it sends immediately,
            // otherwise .then() picks it up — preventing send() on an unopened XHR.
            let xhrReady = false;
            let started = autoStart;
            // `cancelled` is set synchronously by cancel() before the URL resolves.
            // The .then() handler checks this flag early and skips xhr.send() if true.
            // If the XHR is already OPENED, xhr.abort() is called instead (triggers onabort).
            let cancelled = false;
            const task = {
                id,
                file,
                progress: 0,
                status: 'idle',
                start: () => {
                    if (started)
                        return;
                    started = true;
                    if (xhrReady) {
                        xhr.send(file);
                    }
                },
                cancel: () => {
                    cancelled = true;
                    if (xhrReady) {
                        // XHR is OPENED — abort() will fire onabort which updates task status
                        xhr.abort();
                    }
                    else {
                        // XHR is still UNSENT (URL not yet resolved) — abort() is a no-op,
                        // so update task status directly
                        setTasks((ts) => ts.map((t) => (t.id === id ? { ...t, status: 'canceled' } : t)));
                    }
                },
            };
            setTasks((ts) => [...ts, task]);
            getUploadUrl(file)
                .then(({ signedUrl, storagePath, publicUrl, extensionHeaders }) => {
                if (cancelled)
                    return;
                xhr.open('PUT', signedUrl);
                xhr.setRequestHeader('Content-Type', file.type);
                if (extensionHeaders) {
                    Object.entries(extensionHeaders).forEach(([key, value]) => {
                        xhr.setRequestHeader(key, value);
                    });
                }
                xhrReady = true;
                xhr.upload.onprogress = (event) => {
                    const progress = event.lengthComputable
                        ? (event.loaded / event.total) * 100
                        : 0;
                    const status = 'running';
                    setTasks((ts) => ts.map((t) => (t.id === id ? { ...t, status, progress } : t)));
                };
                xhr.onload = () => {
                    if (xhr.status >= 200 && xhr.status < 300) {
                        setTasks((ts) => ts.map((t) => t.id === id
                            ? {
                                ...t,
                                status: 'success',
                                progress: 100,
                                storagePath,
                                publicUrl,
                            }
                            : t));
                    }
                    else {
                        setTasks((ts) => ts.map((t) => t.id === id
                            ? {
                                ...t,
                                status: 'error',
                                error: new Error(`Upload failed: HTTP ${xhr.status}`),
                            }
                            : t));
                    }
                };
                xhr.onerror = () => {
                    setTasks((ts) => ts.map((t) => (t.id === id ? { ...t, status: 'error' } : t)));
                };
                xhr.onabort = () => {
                    setTasks((ts) => ts.map((t) => (t.id === id ? { ...t, status: 'canceled' } : t)));
                };
                if (started) {
                    xhr.send(file);
                }
            })
                .catch((error) => {
                setTasks((ts) => ts.map((t) => (t.id === id ? { ...t, status: 'error', error } : t)));
            });
        });
    };
    const isUploading = tasks.some((t) => t.status === 'running');
    return {
        tasks,
        addFiles,
        isUploading,
    };
}

export { useFirebaseFileUpload };
//# sourceMappingURL=useFirebaseFileUpload.js.map
